import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, ChefHat, Plane, Bot, Package, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { RatingStars } from "@/components/RatingStars";
import { GuidaJrButton } from "@/components/GuidaJrButton";
import { WaitlistModal } from "@/components/WaitlistModal";
import NewsletterForm from "@/components/NewsletterForm";

const AVAILABLE_IDS = ["wild-chef-jr", "corso-impiattamento", "corso-chef-casa", "art-cookbook"];
const EXTRAS = {
  "wild-chef-jr": "4 lezioni illustrate stile manga · ricetta-gioco per bambini · passo-passo con Wilder",
  "corso-impiattamento": "4 video-lezioni · ricetta stellata · passo-passo con Wilder · attestato",
  "corso-chef-casa": "4 video-lezioni · le basi da chef · passo-passo con Wilder · attestato",
  "art-cookbook": "PDF illustrato · 3 capitoli · video di presentazione · ricette del CV dello Chef",
};

const COURSES = [
  { Icon: Package, t: "Pacchetto BASIC", d: "13 corsi fondamentali minfuell: mindset, cucina, creatività e primi passi digitali.", tag: "In arrivo — Fase 3" },
  { Icon: ChefHat, t: "Corsi HORECA", d: "Formazione per ristoranti, hotel e agriturismi: menu d'arte, sala, esperienze memorabili.", tag: "In arrivo — Fase 3" },
  { Icon: Plane, t: "Viaggi & Lifestyle", d: "Vivere e lavorare ovunque: il metodo minfuell369 per un lifestyle da creator.", tag: "In arrivo — Fase 3" },
  { Icon: Bot, t: "AI & Creator", d: "Usare l'intelligenza artificiale per creare contenuti, corsi e business.", tag: "In arrivo — Fase 3" },
  { Icon: GraduationCap, t: "Cucina sana con Wilder", d: "Il primo corso pilota firmato Wilder: ricette d'arte, tecnica e follia creativa.", tag: "In arrivo — Fase 4" },
];

export default function Corsi() {
  const [available, setAvailable] = useState([]);
  const [reviews, setReviews] = useState({});
  const [wlProduct, setWlProduct] = useState(null);
  useEffect(() => {
    api.get("/shop/products").then((r) =>
      setAvailable(AVAILABLE_IDS.map((id) => r.data.find((p) => p.id === id)).filter(Boolean))
    ).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="corsi-page">
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4">minfuell · minfuell369</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">Corsi & Prodotti Digitali</h1>
          <p className="text-sm text-[#C9C4B8] leading-relaxed max-w-2xl">
            I primi corsi firmati Wilder sono già disponibili qui sotto, con accesso immediato all'Academy.
            Il catalogo completo minfuell sta arrivando: parla con Wilder per un'anteprima gratuita del metodo.
          </p>
        </div>
      </section>

      {available.length > 0 && (
        <section className="border-b border-[#2A2A2A]" data-testid="corsi-available">
          <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
            <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">Disponibili ora</p>
            <h2 className="font-display text-lg font-bold mb-10">Inizia oggi, con Wilder come tutor</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
              {available.map((p) => (
                <div key={p.id} className="bg-[#0A0A0A] flex flex-col" data-testid={`corsi-available-${p.id}`}>
                  <div className="aspect-[16/10] overflow-hidden border-b border-[#2A2A2A] relative group">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/70 text-[#E4BD44] text-[10px] uppercase tracking-[0.15em] px-2 py-1">
                      <PlayCircle size={12} /> Accesso immediato
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-xl font-bold leading-snug mb-2">{p.name}</h3>
                    <div className="mb-2"><RatingStars rating={reviews[p.id]} /></div>
                    <p className="text-xs text-[#C9C4B8] leading-relaxed mb-2 flex-1">{p.desc}</p>
                    <p className="text-[11px] text-[#9a9484] mb-5">{EXTRAS[p.id]}</p>
                    <button onClick={() => setWlProduct(p)}
                      className="btn-primary !py-2.5 !px-5 text-xs self-start" data-testid={`corsi-waitlist-${p.id}`}>
                      RICHIEDI INFO
                    </button>
                    {p.id === "wild-chef-jr" && <GuidaJrButton className="mt-4" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <WaitlistModal product={wlProduct || {}} open={!!wlProduct} onOpenChange={(v) => !v && setWlProduct(null)} />

      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">Prossimamente</p>
          <h2 className="font-display text-lg font-bold mb-10">Il catalogo minfuell in arrivo</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
            {COURSES.map((c, i) => (
              <div key={c.t} className="bg-[#0A0A0A] p-8" data-testid={`course-card-${i}`}>
                <c.Icon size={24} className="text-[#E4BD44] mb-5" />
                <h3 className="font-display text-xl font-bold mb-2">{c.t}</h3>
                <p className="text-sm text-[#C9C4B8] leading-relaxed mb-5">{c.d}</p>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37] px-2.5 py-1">{c.tag}</span>
              </div>
            ))}
            <div className="bg-[#121212] text-[#F9F6F0] p-8 flex flex-col justify-between">
              <div>
                <h3 className="font-display text-xl font-bold mb-2">Bundle completi</h3>
                <p className="text-sm text-[#b8b2a4] leading-relaxed mb-5">
                  Pacchetti combinati corsi + coaching 1:1 + esperienze Assaggi d'Arte.
                </p>
              </div>
              <Link to="/wilder" className="btn-primary self-start" data-testid="corsi-wilder-cta">Chiedi a Wilder</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#141414]">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16 text-center">
          <h2 className="font-display text-lg font-bold mb-3">Accedi in anteprima</h2>
          <p className="text-sm text-[#C9C4B8] mb-8">Iscriviti: ti avvisiamo al lancio dei corsi e ricevi subito "Le 3 ricette d'arte di Wilder".</p>
          <div className="max-w-md mx-auto"><NewsletterForm testId="corsi-newsletter" /></div>
        </div>
      </section>
    </div>
  );
}
